import { Separator } from '@neo/primitives/separator'
import { FileQuestion, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { EvalStreamEntry } from 'v2/pages/flow/types'
import { EvalEvents } from 'v2/utils/event-bus/eval-events'
import { useEvalStore } from '../store'
import type { EvalResult } from '../types'
import { NodeSection } from './node-section'

export interface ResultsTreeStateSnapshot {
  evalResults: EvalResult
  events: EvalStreamEntry[]
  isEvalRunning: boolean
  currentRunId: number | null
  currentlyTestingNodeUuid: string | null
  isPaused: boolean
}

interface ResultsTreeProps {
  state?: ResultsTreeStateSnapshot | null
  readOnly?: boolean
}

const buildExpansionMap = (result: EvalResult) => {
  return result.nodes.reduce<Record<string, boolean>>((acc, node) => {
    acc[`${node.nodeUuid}-${node.runId ?? 0}`] = node.isExpanded
    return acc
  }, {})
}

export const ResultsTree = ({ state, readOnly = false }: ResultsTreeProps) => {
  const storeState = useEvalStore((store) => ({
    evalResults: store.evalResults,
    events: store.events,
    isEvalRunning: store.isEvalRunning,
    currentRunId: store.currentRunId,
    currentlyTestingNodeUuid: store.currentlyTestingNodeUuid,
    isPaused: store.isPaused,
    toggleNodeExpanded: store.toggleNodeExpanded,
  }))

  const effectiveState: ResultsTreeStateSnapshot | null = useMemo(
    () =>
      state === undefined
        ? {
            evalResults: storeState.evalResults,
            events: storeState.events,
            isEvalRunning: storeState.isEvalRunning,
            currentRunId: storeState.currentRunId,
            currentlyTestingNodeUuid: storeState.currentlyTestingNodeUuid,
            isPaused: storeState.isPaused,
          }
        : state,
    [
      state,
      storeState.evalResults,
      storeState.events,
      storeState.isEvalRunning,
      storeState.currentRunId,
      storeState.currentlyTestingNodeUuid,
      storeState.isPaused,
    ],
  )

  const [localExpansion, setLocalExpansion] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!readOnly) {
      return
    }
    if (!effectiveState) {
      setLocalExpansion({})
      return
    }
    setLocalExpansion(buildExpansionMap(effectiveState.evalResults))
  }, [effectiveState, readOnly])

  if (!effectiveState) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileQuestion className="h-12 w-12 text-p-300 mb-3" />
        <p className="text-p-500">Test results will appear here after you run a simulation.</p>
      </div>
    )
  }

  const { evalResults, events, isEvalRunning, currentRunId, currentlyTestingNodeUuid, isPaused } =
    effectiveState

  const nodes = evalResults.nodes ?? []

  if (isEvalRunning && nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="h-12 w-12 text-p-300 mb-3 animate-spin" />
        <p className="text-p-500">Simulation started, waiting for results...</p>
      </div>
    )
  }

  if (!isEvalRunning && nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileQuestion className="h-12 w-12 text-p-300 mb-3" />
        <p className="text-p-500">Test results will appear here after you run a simulation.</p>
      </div>
    )
  }

  const nodesByRun = nodes.reduce<Record<number, typeof nodes>>((acc, node) => {
    const runId = node.runId ?? 0
    if (!acc[runId]) acc[runId] = []
    acc[runId].push(node)
    return acc
  }, {})

  const runIds = Object.keys(nodesByRun)
    .map(Number)
    .sort((a, b) => a - b)

  const cancellationsByRun = events
    .filter((event) => event.type === EvalEvents.CANCELLED)
    .reduce<Record<number, EvalStreamEntry>>((acc, event) => {
      const runId =
        event.payload && typeof event.payload === 'object' && 'run_id' in event.payload
          ? ((event.payload as { run_id: number }).run_id ?? 0)
          : 0
      acc[runId] = event
      return acc
    }, {})

  const context = {
    currentlyTestingNodeUuid,
    isPaused,
    currentRunId,
  }

  return (
    <div className="space-y-3">
      {runIds.map((runId) => (
        <div key={`run-${runId}`} className="space-y-3">
          {nodesByRun[runId].map((node) => {
            const key = `${node.nodeUuid}-${node.runId ?? 0}`
            const nodeForRender =
              readOnly && key in localExpansion
                ? { ...node, isExpanded: localExpansion[key] }
                : node

            const handleToggle = () => {
              if (readOnly) {
                setLocalExpansion((prev) => ({
                  ...prev,
                  [key]: !(prev[key] ?? node.isExpanded),
                }))
              } else {
                storeState.toggleNodeExpanded(node.nodeUuid, node.runId)
              }
            }

            return (
              <NodeSection
                key={node.nodeUuid}
                node={nodeForRender}
                context={context}
                onToggle={handleToggle}
              />
            )
          })}

          {cancellationsByRun[runId] && (
            <div className="flex items-center gap-2 w-full">
              <Separator className="flex-1" />
              <p className="text-p-500 text-primary whitespace-nowrap">Simulation cancelled</p>
              <Separator className="flex-1" />
            </div>
          )}
        </div>
      ))}

      {isEvalRunning && runIds.length > 0 && currentRunId !== null && !nodesByRun[currentRunId] && (
        <div className="flex items-center gap-2 rounded-lg border border-st2 bg-background px-4 py-3">
          <Loader2 className="h-4 w-4 text-p-400 animate-spin" aria-hidden />
          <p className="text-p-500">Simulation running, preparing results...</p>
        </div>
      )}
    </div>
  )
}
